import { useMemo } from 'react'
import { motion } from 'motion/react'
import { TeaCard } from '@/entities/product/ui/tea-card'
import { useTeaCategories } from '@/entities/product/api/use-categories'
import type { Tea, TeaCategory, TeaSubcategory } from '@/entities/product'

export interface CatalogGroupedSectionsProps {
  /** Список чая для группировки */
  teas: Tea[]
  /** Состояние загрузки */
  isLoading?: boolean
  /** ID выбранной категории (если выбрана) - показываем только её подкатегории */
  selectedCategoryId?: string
  /** ID выбранной подкатегории (если выбрана) - показываем только её */
  selectedSubcategoryId?: string
}

/**
 * Секция каталога (категория с подкатегориями)
 */
interface CatalogSection {
  category: TeaCategory
  /** Товары напрямую в категории (без подкатегории) */
  items: Tea[]
  /** Подкатегории с товарами */
  subsections: Array<{
    subcategory: TeaSubcategory
    items: Tea[]
  }>
}

/**
 * Группирует товары по категориям и подкатегориям
 * Простая логика: сравнение по ID
 */
function groupProductsByCategories(
  products: Tea[],
  categories: TeaCategory[]
): CatalogSection[] {
  // Сортируем категории по orderIndex/sortOrder
  const sortedCategories = [...categories].sort((a, b) => {
    const orderA = a.orderIndex ?? a.sortOrder ?? 999
    const orderB = b.orderIndex ?? b.sortOrder ?? 999
    return orderA - orderB
  })

  return sortedCategories.map((category) => {
    // Товары в этой категории
    const categoryProducts = products.filter((p) => p.categoryId === category.id)

    // Товары без подкатегории (напрямую в категории)
    const directItems = categoryProducts.filter((p) => !p.subcategoryId)

    // Сортируем подкатегории
    const sortedSubcategories = [...(category.subcategories || [])].sort((a, b) => {
      const orderA = a.orderIndex ?? a.sortOrder ?? 999
      const orderB = b.orderIndex ?? b.sortOrder ?? 999
      return orderA - orderB
    })

    // Группируем по подкатегориям
    const subsections = sortedSubcategories.map((subcategory) => ({
      subcategory,
      items: categoryProducts.filter((p) => p.subcategoryId === subcategory.id),
    }))

    return {
      category,
      items: directItems,
      subsections,
    }
  })
}

/**
 * Виджет: Группировка каталога чая по секциям
 * 
 * Отображает чай в секциях с заголовками и декоративными элементами.
 * Показывает все категории из базы данных в порядке sortOrder.
 * Использует категории с бэкенда для группировки.
 */
export function CatalogGroupedSections({
  teas,
  isLoading = false,
  selectedCategoryId,
  selectedSubcategoryId,
}: CatalogGroupedSectionsProps) {
  // Получаем категории с бэкенда
  const { data: categoriesData, isLoading: categoriesLoading } = useTeaCategories()
  const allCategories = categoriesData?.data ?? []
  
  // Если выбрана категория - фильтруем только её
  const categories = useMemo(() => {
    if (selectedCategoryId) {
      return allCategories.filter(cat => cat.id === selectedCategoryId)
    }
    return allCategories
  }, [allCategories, selectedCategoryId])
  
  // Если выбрана подкатегория - не показываем заголовки секций
  const showSubcategoryHeaders = !selectedSubcategoryId

  // Фильтруем невалидные товары
  const validTeas = useMemo(
    () => teas.filter((t): t is Tea => t != null && t.id != null),
    [teas]
  )

  // Группируем товары по категориям
  const sections = useMemo(
    () => groupProductsByCategories(validTeas, categories),
    [validTeas, categories]
  )

  // Skeleton загрузки
  if (isLoading || categoriesLoading) {
    return (
      <div className="px-4 space-y-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4">
            <div className="h-6 bg-gray-200 dark:bg-dark-elevated rounded animate-pulse w-48" />
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-64 bg-gray-200 dark:bg-dark-elevated rounded-[16px] animate-pulse" />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Если категорий нет, но есть товары - показываем плоский список
  if (sections.length === 0 && validTeas.length > 0) {
    return (
      <div className="px-4 pb-6">
        <div className="grid grid-cols-2 gap-4">
          {validTeas.map((tea, index) => (
            <motion.div
              key={tea.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(index * 0.05, 0.5) }}
            >
              <TeaCard tea={tea} />
            </motion.div>
          ))}
        </div>
      </div>
    )
  }

  // Фильтруем пустые секции для отображения
  const nonEmptySections = sections.filter(
    (s) => s.items.length > 0 || s.subsections.some((sub) => sub.items.length > 0)
  )

  if (nonEmptySections.length === 0) {
    return null
  }

  // Если выбрана подкатегория - показываем плоский список
  if (selectedSubcategoryId) {
    return (
      <div className="px-4 pb-6">
        <div className="grid grid-cols-2 gap-4">
          {validTeas.map((tea, index) => (
            <motion.div
              key={tea.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(index * 0.05, 0.5) }}
            >
              <TeaCard tea={tea} />
            </motion.div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 space-y-8 pb-6">
      {nonEmptySections.map((section, sectionIndex) => (
        <motion.div
          key={section.category.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(sectionIndex * 0.1, 0.5) }}
          className="space-y-4"
        >
          {/* Заголовок категории - скрываем если выбрана только одна категория */}
          {!selectedCategoryId && (
            <div>
              <h3 className="text-[20px] font-bold text-ink dark:text-white mb-1">
                {section.category.name}
              </h3>
              <div className="h-0.5 w-12 bg-primary dark:bg-primary-dark rounded-full" />
            </div>
          )}

          {/* Товары напрямую в категории */}
          {section.items.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {section.items.map((tea, teaIndex) => (
                <motion.div
                  key={tea.id}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.2,
                    delay: Math.min(teaIndex * 0.03, 0.3),
                  }}
                >
                  <TeaCard tea={tea} />
                </motion.div>
              ))}
            </div>
          )}

          {/* Подкатегории */}
          {section.subsections.map((subsection) => {
            if (subsection.items.length === 0) return null
            
            return (
              <div key={subsection.subcategory.id} className="space-y-3 mt-6">
                {/* Заголовок подкатегории */}
                <div>
                  <h4 className="text-[16px] font-semibold text-ink dark:text-white">
                    {subsection.subcategory.name}
                  </h4>
                  {subsection.subcategory.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {subsection.subcategory.description}
                    </p>
                  )}
                </div>

                {/* Товары в подкатегории */}
                <div className="grid grid-cols-2 gap-4">
                  {subsection.items.map((tea, teaIndex) => (
                    <motion.div
                      key={tea.id}
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.2,
                        delay: Math.min(teaIndex * 0.03, 0.3),
                      }}
                    >
                      <TeaCard tea={tea} />
                    </motion.div>
                  ))}
                </div>
              </div>
            )
          })}
        </motion.div>
      ))}
    </div>
  )
}
